import { memo, useState, useMemo, useCallback } from 'react';
import { Loader2, ChevronRight, FileText, ExternalLink } from 'lucide-react';
import { getTotalInventory } from '@flote/utils';
import { B2BWaveProductStatuses } from '@flote/constants';
import { useAuth, useCart, useCartDrawer, useProductInCart, useFunnelTracking, useProductFiles } from '@flote/app-core';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  VisuallyHidden,
  Card,
  CardContent,
  SmartPrice,
  useSmartPrice,
  QuantityCounter,
  Button,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@flote/ui';
import type { EnrichedB2BWaveProduct } from '@flote/types/product';
import { useModal, ModalType } from '../../lib/modal';
import { BackInStockSubscribeForm } from './BackInStockSubscribeForm';

interface ProductDetailModalProps {
  product: EnrichedB2BWaveProduct;
}

function ProductImage({ image, name }: { image: string; name: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-white p-4">
      <img
        src={image}
        alt={name}
        className="max-h-full max-w-full object-contain animate-in fade-in duration-300"
      />
    </div>
  );
}

function ExpandableDescription({ description }: { description: string }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!description) return null;

  return (
    <div className="mb-4 mt-3">
      <div className="relative">
        <div
          className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
            isExpanded ? 'max-h-[2000px]' : 'max-h-[4.5rem]'
          }`}
        >
          <p
            className="text-sm text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
        <div
          className={`pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-background to-transparent transition-opacity duration-300 ${
            isExpanded ? 'opacity-0' : 'opacity-100'
          }`}
        />
      </div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="mt-1 text-sm font-medium text-primary transition-colors hover:underline"
      >
        {isExpanded ? 'Show less' : 'Read more'}
      </button>
    </div>
  );
}

function ProductBreadcrumb({ categoryPath }: { categoryPath?: Array<{ id: string; name: string }> }) {
  if (!categoryPath || categoryPath.length === 0) return null;

  return (
    <div className="mb-3 flex items-center text-xs text-muted-foreground">
      {categoryPath.map((category, index) => (
        <span key={category.id} className="flex items-center">
          <span className="font-medium">{category.name}</span>
          {index < categoryPath.length - 1 && (
            <ChevronRight className="mx-1 h-3 w-3 text-muted-foreground/70" />
          )}
        </span>
      ))}
    </div>
  );
}

function ProductPriceCard({
  product,
  packUnit,
  unitsPerCase,
  pricePerUnit,
  inventory,
  backordered,
  quantity = 1,
}: {
  product: EnrichedB2BWaveProduct;
  packUnit: string;
  unitsPerCase?: number | null;
  pricePerUnit: string | null;
  inventory?: number | null;
  backordered?: boolean;
  quantity?: number;
}) {
  const isOutOfStock = inventory === 0 || inventory === null || backordered;

  return (
    <Card className="mb-4 mt-3 bg-transparent">
      <CardContent className="p-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <SmartPrice product={product} quantity={quantity} size="lg" showLabel={true} />
            <p className="text-sm text-muted-foreground">per {packUnit}</p>
          </div>

          {unitsPerCase && unitsPerCase > 1 && pricePerUnit && (
            <div className="text-sm text-muted-foreground">
              Contains {unitsPerCase} units at {pricePerUnit} per unit
            </div>
          )}

          {backordered ? (
            <div className="mt-1 text-sm font-medium text-red-600">Out of Stock</div>
          ) : (
            inventory !== undefined && (
              <div className={`mt-1 text-sm font-medium ${isOutOfStock ? 'text-red-600' : 'text-emerald-600'}`}>
                {isOutOfStock
                  ? 'Out of Stock'
                  : inventory < 5
                    ? `Only ${inventory} left in stock!`
                    : `${inventory} in stock`}
              </div>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ProductDetails({ product }: { product: EnrichedB2BWaveProduct }) {
  const { data: filesResponse, isLoading: isLoadingFiles } = useProductFiles(product.id);
  const productFiles = filesResponse?.status === 200 ? filesResponse.body : [];

  // Prefer the variant-sourced Metrc units-per-case; fall back to legacy packageQuantity.
  const unitsPerCase = product.unitsPerCase ?? product.packageQuantity ?? null;

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="details">
        <AccordionTrigger className="text-sm">Additional Details</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-3 text-sm">
            {unitsPerCase && unitsPerCase > 1 && (
              <div className="flex gap-2">
                <span className="font-medium">Units per case:</span>
                <span className="text-muted-foreground">{unitsPerCase}</span>
              </div>
            )}

            {product.categoryPath && product.categoryPath !== 'Accessory' && (
              <div className="flex gap-2">
                <span className="font-medium">Type:</span>
                <span className="text-muted-foreground">{product.categoryPath}</span>
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>

      {(isLoadingFiles || productFiles.length > 0) && (
        <AccordionItem value="files">
          <AccordionTrigger className="text-sm">
            <span className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Documents & Files
              {productFiles.length > 0 && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                  {productFiles.length}
                </span>
              )}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            {isLoadingFiles ? (
              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading files...
              </div>
            ) : (
              <div className="space-y-2">
                {productFiles.map((file) => (
                  <a
                    key={file.id}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md border border-border/50 p-2 text-sm transition-colors hover:bg-muted/50"
                  >
                    <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{file.title}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {file.filename}
                      </div>
                    </div>
                    <ExternalLink className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  </a>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      )}

      <AccordionItem value="usage">
        <AccordionTrigger className="text-sm">Usage Instructions</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>For adult use only. Keep out of reach of children and pets.</p>
            <p>Follow local regulations regarding use and consumption.</p>
            {product.categoryPath === 'Edibles' && (
              <p>Start with a small amount and wait at least 90 minutes before consuming more.</p>
            )}
            {product.categoryPath === 'Flower' && (
              <p>Store in a cool, dry place away from direct sunlight.</p>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="legal">
        <AccordionTrigger className="text-sm">Legal Information</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Products contain cannabis which can be intoxicating and may be habit forming.</p>
            <p>Cannabis can impair concentration, coordination, and judgment.</p>
            <p>Do not operate a vehicle or machinery under the influence of this drug.</p>
            <p>There may be health risks associated with consumption of these products.</p>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function ProductActions({
  totalInventory,
  quantity,
  onQuantityChange,
  onAddToCart,
  isLoading,
  minOrderQuantity,
  maxOrderQuantity,
  orderIncrement,
}: {
  totalInventory: number;
  quantity: number;
  onQuantityChange: (value: number) => void;
  onAddToCart: () => void | Promise<void>;
  isLoading: boolean;
  minOrderQuantity?: number;
  maxOrderQuantity?: number;
  orderIncrement?: number;
}) {
  const step = orderIncrement ?? 1;
  const effectiveMin = minOrderQuantity ?? 1;
  const effectiveMax = Math.min(maxOrderQuantity ?? totalInventory, totalInventory);
  const belowMinInventory = effectiveMin > totalInventory && totalInventory > 0;

  const handleAddToCart = async () => {
    try {
      await onAddToCart();
    } catch (error) {
      console.error('Add to cart error:', error);
    }
  };

  return (
    <div className="space-y-4">
      {minOrderQuantity && minOrderQuantity > 1 && (
        <p className="text-sm text-muted-foreground">Minimum order: {minOrderQuantity}</p>
      )}
      {maxOrderQuantity && (
        <p className="text-sm text-muted-foreground">Maximum order: {maxOrderQuantity}</p>
      )}
      {orderIncrement && orderIncrement > 1 && (
        <p className="text-sm text-muted-foreground">Sold in increments of {orderIncrement}</p>
      )}

      <div className="flex items-center gap-4">
        <span className="text-sm font-medium">Quantity:</span>
        <QuantityCounter
          value={quantity}
          onChange={onQuantityChange}
          min={effectiveMin}
          max={effectiveMax}
          step={step}
          disabled={isLoading || belowMinInventory}
        />
      </div>

      {belowMinInventory && (
        <p className="text-sm text-amber-600">
          Minimum order is {effectiveMin} but only {totalInventory} available
        </p>
      )}

      <Button
        onClick={handleAddToCart}
        size="lg"
        disabled={isLoading || totalInventory <= 0 || belowMinInventory}
        className="w-full"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Adding to Cart...
          </>
        ) : (
          'Add to Cart'
        )}
      </Button>
    </div>
  );
}

export const ProductDetailModal = memo(({ product }: ProductDetailModalProps) => {
  const { closeModal, openModal } = useModal();
  const { isAuthenticated } = useAuth();
  const { isInCart, quantity: currentQuantity } = useProductInCart(product.id.toString());
  const { addItem, isLoading } = useCart();
  const { open: openCart } = useCartDrawer();
  const { trackProductAddedToCart } = useFunnelTracking();

  const [modalQuantity, setModalQuantity] = useState(product.minOrderQuantity ?? 1);

  const totalInventory = useMemo(() => getTotalInventory(product), [product]);

  const isBackordered = useMemo(() => {
    const hasBackorderStatus = product.statusId === B2BWaveProductStatuses.BACKORDER;
    return hasBackorderStatus || Boolean(product.canBackorder && totalInventory <= 0);
  }, [product.statusId, product.canBackorder, totalInventory]);

  const categoryPath = useMemo(
    () => (product.categoryPath ? [{ id: product.categoryPath, name: product.name }] : []),
    [product.categoryPath, product.name]
  );

  const primaryPrice = useMemo(() => {
    if (!product.pricing) {
      return product.priceMsrp || 0;
    }
    const price = product.pricing.price;
    return price ? parseFloat(price) : product.priceMsrp || 0;
  }, [product.pricing, product.priceMsrp]);

  // Units per master case. Prefer the variant-sourced Metrc value; fall back to
  // the legacy B2BWave packageQuantity for products that predate native catalog.
  const unitsPerCase = product.unitsPerCase ?? product.packageQuantity ?? null;

  // Effective per-case price from the shared pricing engine (bulk/customer/buyer
  // tiers), so the per-unit breakdown matches the price SmartPrice displays
  // rather than the pre-discount standard price.
  const priceInfo = useSmartPrice(product, modalQuantity);
  const pricePerUnit =
    unitsPerCase && unitsPerCase > 1 && priceInfo.price > 0
      ? `$${(priceInfo.price / unitsPerCase).toFixed(2)}`
      : null;

  const handleQuantityChange = useCallback((newQuantity: number) => {
    setModalQuantity(newQuantity);
  }, []);

  const handleAddToCart = useCallback(async () => {
    if (!isAuthenticated) {
      openModal(ModalType.AUTH_MODAL, {});
      return;
    }
    trackProductAddedToCart({
      productId: product.id.toString(),
      productName: product.name,
      quantity: modalQuantity,
      price: primaryPrice,
      brandId: product.brandId?.toString(),
    });
    await addItem({
      product,
      quantity: modalQuantity,
    });
    closeModal();
    openCart();
  }, [addItem, product, modalQuantity, openCart, closeModal, openModal, isAuthenticated, trackProductAddedToCart, primaryPrice]);

  return (
    <Dialog open={true} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-4xl overflow-y-auto p-0 sm:w-full">
        <DialogHeader className="sr-only">
          <VisuallyHidden>
            <DialogTitle>{product.name}</DialogTitle>
          </VisuallyHidden>
        </DialogHeader>

        <div className="flex flex-col md:flex-row">
          <div className="flex w-full flex-shrink-0 items-center justify-center md:w-1/2">
            <div className="aspect-square max-h-[40vh] max-w-full md:max-h-none md:w-full">
              <ProductImage image={product.imageUrl} name={product.name} />
            </div>
          </div>

          <div className="flex w-full flex-col p-4 sm:p-6 md:w-1/2">
            <div className="space-y-4">
              <ProductBreadcrumb categoryPath={categoryPath} />

              <h2 className="text-2xl font-medium tracking-tight">{product.name}</h2>

              {isInCart && (
                <p className="mb-2 text-sm text-green-600">✓ {currentQuantity} in cart</p>
              )}

              <ExpandableDescription description={product.description} />

              <ProductPriceCard
                product={product}
                packUnit="case"
                unitsPerCase={unitsPerCase}
                pricePerUnit={pricePerUnit}
                inventory={Math.trunc(totalInventory)}
                backordered={isBackordered}
                quantity={modalQuantity}
              />

              <ProductDetails product={product} />

              <div className="border-t border-border/20 pt-4">
                {isBackordered ? (
                  <BackInStockSubscribeForm productId={product.id} productName={product.name} />
                ) : (
                  <ProductActions
                    totalInventory={totalInventory}
                    quantity={modalQuantity}
                    onQuantityChange={handleQuantityChange}
                    onAddToCart={handleAddToCart}
                    isLoading={isLoading}
                    minOrderQuantity={product.minOrderQuantity}
                    maxOrderQuantity={product.maxOrderQuantity}
                    orderIncrement={product.orderIncrement}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

ProductDetailModal.displayName = 'ProductDetailModal';
